import { useState } from 'react';

import { motion } from 'framer-motion';
import styled from 'styled-components';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { PreviewFields } from '@components/form-builder/preview-fields';
import { ViewportType } from '@components/form-builder/state';
import { mergePredefinedOptions } from '@components/form-builder/utils';
import { ViewportActions } from '@components/form-builder/viewport-actions';
import { Header } from '@components/header';
import { IconForm } from '@components/icons/form';
import { InfoBox } from '@components/info-box';
import { RelativeSpinner } from '@components/loading-spinner';
import { hideOverlay, useOverlay } from '@components/overlay';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useJourneyActionForm } from 'api/journey-action-forms/hooks';
import { useFormBuilderLists } from 'api/lists/hooks';

export const useOverlayPreview = () => {
  return useOverlay(OverlayPreviewModal);
};

const OverlayPreviewModal = ({ formId }: { formId: string }) => {
  const { t } = useScopedTranslate(locale);
  const [viewport, setViewport] = useState<ViewportType>('desktop');
  const formQuery = useJourneyActionForm(formId);
  const { data: lists, isLoading: listsLoading } = useFormBuilderLists();

  if (formQuery.isLoading || listsLoading) {
    return <RelativeSpinner />;
  }

  if (!formQuery.data) {
    return (
      <Container>
        <Header
          actions={
            <Stack space={6} orientation="horizontal" align="flex-start" justify="center">
              <ButtonGroup align="right">
                <Button
                  variant="secondary"
                  text={t('core.close')}
                  onClick={hideOverlay}
                  autoFocus
                />
              </ButtonGroup>
            </Stack>
          }
        />
        <PreviewContainer>
          <InfoBox text={t('noForm')} variant="orange" icon={<IconForm />} />
        </PreviewContainer>
      </Container>
    );
  }

  const form = formQuery.data;

  const fields_with_predefined_options = mergePredefinedOptions(
    form.fieldsConfig.fields ?? [],
    lists
  );

  return (
    <Container>
      <Header
        title={
          <NameContainer>
            <Text font="bold18" text={form.name} dataTestid="form-title" />
          </NameContainer>
        }
        actions={
          <Stack space={6} orientation="horizontal" align="flex-start" justify="center">
            <ViewportActions onViewportSelect={setViewport} selectedViewport={viewport} />
            <ButtonGroup align="right">
              <Button variant="secondary" text={t('core.close')} onClick={hideOverlay} autoFocus />
            </ButtonGroup>
          </Stack>
        }
      />
      <PreviewContainer>
        <PreviewFields fields={fields_with_predefined_options} viewport={viewport} />
      </PreviewContainer>
    </Container>
  );
};

const PreviewContainer = styled.div({
  position: 'relative',
  flexGrow: 1,
  overflow: 'auto',
});

const NameContainer = styled.div({
  ...lineClamp('-webkit-box', 2, true),
});

const Container = styled(motion.div)({
  zIndex: 99999,
  position: 'fixed',
  top: pxToRem(40),
  left: pxToRem(40),
  right: pxToRem(40),
  bottom: pxToRem(40),
  backgroundColor: theme.color.backgroundPrimary,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: pxToRem(8),
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
});

const locale = {
  en: {
    noForm: 'Unable to preview form',
  },
};
